import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import * as d3 from 'd3';
import * as d3Scale from 'd3-scale';
import * as d3Shape from 'd3-shape';

import { ColorLevel, ColorName, ColorsService, NumbersService } from '@common/services';

import { DataPiePoint, Placement, SidePlacementEnum } from '../../../models';
import { BaseChartComponent } from '../base';
import { ChartingService } from '../../../services';

@Component({
    selector: 'aa-donut2',
    templateUrl: './donut2.component.html',
    styleUrls: [
        '../../../container.scss',
        './donut2.component.scss'
    ],
    providers: [
        {
            provide: ChartingService,
            useValue: ChartingService
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class Donut2Component extends BaseChartComponent<DataPiePoint[]> implements OnInit, OnChanges
{
    @Input() isClickable: boolean = false;
    @Input() activatedKey: any;
    @Output() pieClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() sliceHovered: EventEmitter<any> = new EventEmitter<any>();
    @Output() sliceLeft: EventEmitter<any> = new EventEmitter<any>();
    // #region Properties
    private donutPies: DataPiePoint[];
    private translate2Center: string;
    private radius: number;
    private innerRadius: number;
    private outerRadius: number;
    private scaleInnerRadius: number = 0.75 / 2;
    private scaleOuterRadius: number = 0.75;
    private scaleInnerRadius2 = 2.6;
    private scaleOuterRadius2 = 1.5;
    private coloredArc: any;
    private outerArc: any;
    private explodedColoredArc: any;
    private pie: any;
    private circleText: any;
    private centerText1: any;
    private total: number = 0;
    private fontSizeScaleLabel = d3Scale.scaleLinear()
        .domain([20, 60])
        .range([8, 10]);
    private slices: any;
    private labels: any;
    private lines: any;
    private readonly vertiSpacer: number = 7;
    private readonly vertiOffset: number = 14;
    private readonly horizMargin: number = 7;
    // #endregion

    constructor (
        private elementRef: ElementRef,
        private chartingService: ChartingService,
        private numbersService: NumbersService
    )
    {
        super();
    }

    ngOnInit ()
    {
        this.transformData();
    }

    ngOnChanges (changes: SimpleChanges): void
    {
        this.selectActiveSliceByKey(changes.activatedKey?.currentValue);
    }

    protected drawChart (): void
    {
        this.initSvg();

        this.slices = this.gpeChart
            .append('g')
            .attr('class', 'gpeSlices')
            .attr('transform', this.translate2Center)
            .selectAll('.arc')
            .data(this.pie(this.donutPies))
            .enter()
            .append('g')
            .attr('class', () =>
                this.isClickable ? 'arc clickable' : 'arc'
            );

        this.slices.append('path')
            .attr('d', this.coloredArc)
            .style('fill', (d: any): string =>
                (d.data as DataPiePoint).color
            );
        this.addLabels();
        this.computePosition();
        this.addLines();
        this.positionLabels();

        this.initCircle();
        this.drawTotalCircle();

        this.addBehavior(this.slices);
        this.addBehavior(this.labels);
        this.addBehavior(this.lines);
    }

    protected initSvg (): void
    {
        this.baseInitSvg();

        this.translate2Center = `translate(${this.dimensions.boundedWidth / 2},${this.dimensions.boundedHeight / 2})`;
        this.radius = Math.min(this.dimensions.boundedWidth, this.dimensions.boundedHeight) / 2 - 10;
        this.innerRadius = this.radius * this.scaleInnerRadius;
        this.outerRadius = this.radius * this.scaleOuterRadius;

        this.coloredArc = d3Shape.arc()
            .innerRadius(this.innerRadius)
            .outerRadius(this.outerRadius);

        this.explodedColoredArc = d3Shape.arc()
            .innerRadius(this.innerRadius + 7)
            .outerRadius(this.outerRadius + 7);

        this.outerArc = d3Shape.arc()
            .innerRadius(this.innerRadius * this.scaleInnerRadius2)
            .outerRadius(this.innerRadius * this.scaleOuterRadius2);

        this.pie = d3Shape.pie()
            .value((d: any) =>
                d.value
            )
            .sortValues(null);
    }

    private transformData ()
    {
        // Alternance des données : plus grand d'un côté, le suivant de l'autre; etc.
        const tempData: DataPiePoint[] = this.data;
        this.total = tempData
            .reduce((prev: number, curr: DataPiePoint) => prev += curr.value, 0);

        tempData.sort((itema: DataPiePoint, itemb: DataPiePoint)=>
            itema.value - itemb.value
        );

        this.donutPies = [];
        while (tempData.length > 0)
        {
            tempData.reverse();
            this.donutPies.push(tempData.shift());
        }
    }

    private initCircle ()
    {
        this.circleText = this.gpeChart
            .append('circle')
            .attr('transform', this.translate2Center)
            .attr('cx', 0)
            .attr('cy', 0)
            .attr('r', 2 * this.innerRadius / 3)
            .style('fill', 'transparent');

        this.centerText1 = this.gpeChart
            .append('text')
            .attr('transform', this.translate2Center)
            .attr('class', 'center-txt value')
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .style('font-weight', 'bold')
            .style('font-size', this.fontSizeScaleLabel(this.innerRadius));
    }

    private drawTotalCircle ()
    {
        this.circleText
            .style('fill', ColorsService.getBasicColor(ColorLevel.Normal, ColorName.Grey2));
        this.centerText1
            .text(() =>
                this.numbersService.toFixed(this.total, 0)
            )
            .style('fill', ColorsService.getBasicColor(ColorLevel.Normal, ColorName.Black));
    }

    private computePosition (): void
    {
        const items: any = Array.from(this.elementRef.nativeElement.querySelectorAll('.tspan-label'))
            .map((eleRef: any): any=>
                eleRef.getBBox()
            );
        this.slices.each((d: any, i: number): any =>
            (d.data as DataPiePoint).labelBoundingBox = items[i]
        );

        this.slices.each((d: any): void=>
        {
            const pos = this.outerArc.centroid(d);
            const coeff = this.sidePlacement(d);

            pos[0] = coeff * (30 + this.outerRadius);
            (d.data as DataPiePoint).placement = {
                xyLines: [this.coloredArc.centroid(d), this.outerArc.centroid(d), pos],
                coeff
            } as Placement;
        });

        this.donutPies.forEach((dpp: DataPiePoint): void=>
        {
            dpp.placement.vpos = {
                top: dpp.placement.xyLines[2][1],
                bottom: dpp.placement.xyLines[2][1] + dpp.labelBoundingBox.height
            };
        });

        this.donutPies
            .filter((dpp: DataPiePoint): boolean =>
                dpp.placement.coeff === SidePlacementEnum.right
            )
            .forEach((curr: DataPiePoint, idx: number, collection: DataPiePoint[]): void =>
            {
                const prev = collection[idx - 1];

                if (curr !== undefined && prev !== undefined)
                {
                    if (curr.placement.vpos.top < prev.placement.vpos.bottom + this.vertiSpacer)
                    {
                        curr.placement.vpos.top = prev.placement.vpos.bottom + this.vertiOffset;
                    }
                }

                curr.placement.xyLines[1][1] = curr.placement.vpos.top;
                curr.placement.xyLines[2][1] = curr.placement.vpos.top;
                curr.placement.xyLines[2][0] = this.xRight - this.horizMargin;
                curr.placement.xLabelTranslation = this.xRight - curr.labelBoundingBox.width - this.horizMargin;
            });

        this.donutPies
            .filter((dpp: DataPiePoint): boolean =>
                dpp.placement.coeff === SidePlacementEnum.left
            )
            .forEach((curr: DataPiePoint, idx: number, collection: DataPiePoint[]): void =>
            {
                const prev = collection[idx + 1];

                if (curr !== undefined && prev !== undefined)
                {
                    if (curr.placement.vpos.top < prev.placement.vpos.bottom + this.vertiSpacer)
                    {
                        curr.placement.vpos.top = prev.placement.vpos.bottom + this.vertiOffset;
                    }
                }

                curr.placement.xyLines[1][1] = curr.placement.vpos.top;
                curr.placement.xyLines[2][1] = curr.placement.vpos.top;
                curr.placement.xyLines[2][0] = this.xLeft + this.horizMargin;
                curr.placement.xLabelTranslation = this.xLeft + this.horizMargin;
            });
    }

    private addLabels (): void
    {
        this.labels = this.gpeChart
            .append('g')
            .attr('transform', this.translate2Center)
            .attr('class', 'gpeLabels')
            .selectAll('.labelName')
            .data(this.pie(this.donutPies))
            .enter()
            .append('g')
            .attr('class', 'labelName');

        const texts = this.labels
            .append('text')
            .attr('dy', '.35em')
            .attr('dominant-baseline', 'middle')
            .style('font-size', '12px');

        texts.append('tspan')
            .attr('class', (): string =>
                this.isClickable ? 'tspan-label clickable' : 'tspan-label'
            )
            .attr('id', (_: any, i: number): string =>
                `id-${i}`
            )
            .text((d: any): string=>
                (d.data as DataPiePoint).label
            )
            .style('fill', (d: any): string =>
                (d.data as DataPiePoint).color
            )
            .style('text-decoration', 'none');
    }

    private positionLabels (): void
    {
        this.labels
            .attr('transform', (d: any): string =>
                `translate(${(d.data as DataPiePoint).placement.xLabelTranslation}, ${(d.data as DataPiePoint).placement.xyLines[2][1]})`
            )
            .insert('rect', 'text')
            .attr('x', (d: any): number =>
                (d.data as DataPiePoint).labelBoundingBox.x - 3
            )
            .attr('y', (d: any): number =>
                (d.data as DataPiePoint).labelBoundingBox.y - 3
            )
            .attr('width', (d: any): number =>
                (d.data as DataPiePoint).labelBoundingBox.width + 6
            )
            .attr('height', (d: any): number =>
                (d.data as DataPiePoint).labelBoundingBox.height + 6
            )
            .style('fill', 'white')
            .style('stroke', (d: any): string=>
                (d.data as DataPiePoint).color
            );
    }

    private addLines (): void
    {
        this.lines = this.gpeChart
            .insert('g', '.gpeSlices')
            .attr('class', 'gpeLines')
            .attr('transform', this.translate2Center)
            .selectAll('polyline')
            .data(this.pie(this.donutPies))
            .enter()
            .append('polyline')
            .attr('class', '.line')
            .attr('points', (d: any) =>
                [
                    (d.data as DataPiePoint).placement.xyLines[0],
                    (d.data as DataPiePoint).placement.xyLines[1],
                    (d.data as DataPiePoint).placement.xyLines[2]]
            )
            .style('fill', 'transparent')
            .style('stroke', (d: any) =>
                (d.data as DataPiePoint).color
            );
    }

    private addBehavior (selection: any)
    {
        selection
            .on(
                'mouseover',
                (_: any, d: any): void =>
                {
                    this.sliceMouseover(d.data);
                    this.sliceHovered.emit(d.data.dataKey ?? d.data);
                }
            )
            .on(
                'mouseout',
                (_: any, d: any): void =>
                {
                    this.sliceMouseout(d.data);
                    this.sliceLeft.emit(d.data.dataKey ?? d.data);
                }
            )
            .on(
                'click',
                (_: any, d: any): void =>
                {
                    if (this.isClickable)
                    {
                        this.pieClicked.emit(d.data.dataKey ?? d.data);
                    }
                }
            );
    }

    private selectActiveSlice (d: DataPiePoint): any
    {
        const key = d.dataKey || d.value;

        return this.slices
            .filter((d: any): boolean =>
                ((d.data as DataPiePoint).dataKey || (d.data as DataPiePoint).value) === key
            )
            .node();
    }

    private selectActiveSliceByKey (key?: string | number): any
    {
        if (key)
        {
            const dataPiePoint = this.donutPies.find((dataPiePoint: DataPiePoint): boolean =>
                dataPiePoint.dataKey === key
            );

            if (dataPiePoint)
            {
                this.sliceMouseover(dataPiePoint);
            }
        }
        else
        {
            if (this.slices)
            {
                this.slices.each((d: any): void =>
                    this.sliceMouseout(d.data as DataPiePoint)
                );
            }
        }
    }

    private sliceMouseover (d: DataPiePoint): void
    {
        const sliceNode = this.selectActiveSlice(d);
        const pathNode = d3.select(sliceNode).select('path');

        pathNode
            .attr('d', this.explodedColoredArc)
            .style('fill', d.colorHover);
        this.circleText
            .style('fill', d.colorHover);
        this.centerText1
            .text((): string =>
                this.numbersService.toFixed(d.value, 0)
            )
            .style('fill', d.color);
    }

    private sliceMouseout (d: DataPiePoint)
    {
        const sliceNode = this.selectActiveSlice(d);
        const pathNode = d3.select(sliceNode).select('path');

        pathNode
            .attr('d', this.coloredArc)
            .style('fill', d.color);

        this.drawTotalCircle();
    }

    private midAngle (d: any)
    {
        return d.startAngle + (d.endAngle - d.startAngle) / 2;
    }

    private sidePlacement (d: any): number
    {
        return this.midAngle(d) < Math.PI ? SidePlacementEnum.right : SidePlacementEnum.left;
    }
}
